import { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { ChevronDownIcon, InfoIcon } from '@rsos/base-assets/icons';
import { ConditionalDisplay } from '@rsos/base-components';
import { RotatedButton } from '../ChatCard/LiveChatCard.styles';
import styles from './ProtectedEmployeeCard.module.css';
import {
  PROTECTED_EMPLOYEE_CARD_TITLE,
  PROTECTED_EMPLOYEE_CONSENT_LABEL,
  PROTECTED_EMPLOYEE_DISPLAY_NAME,
  PROTECTED_EMPLOYEE_ENROLLMENT_SUBTITLE,
  PROTECTED_EMPLOYEE_SAFETY_CONTACT_LABEL,
} from './constants';
import {
  getProtectedEmployeeSafetyContactLine,
  selectProtectedEmployeeForCall,
} from './selectProtectedEmployee';

type ProtectedEmployeeCardProps = {
  /**
   * Overview tab: start collapsed and expand/collapse inline.
   * Data tab: always show the full card.
   */
  summarized?: boolean;
};

/**
 * Additional Emergency Profile for a Protected Employee on Unite ECC. The
 * enrolled caller's phone stays hidden; only org-provided GSOC escalation
 * info is shown.
 */
const ProtectedEmployeeCard = ({
  summarized = false,
}: ProtectedEmployeeCardProps) => {
  const profile = useSelector(selectProtectedEmployeeForCall, shallowEqual);
  const selectedCallerID = useSelector(
    (state: { emergencyCalls?: { selectedCallerID?: string | null } }) =>
      state.emergencyCalls?.selectedCallerID,
  );
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsExpanded(false);
  }, [selectedCallerID]);

  const hasProfile = profile !== null;
  const organization = profile?.organization?.trim() || null;
  const safetyContactLine = getProtectedEmployeeSafetyContactLine(profile);
  const showDetails = !summarized || isExpanded;

  const handleToggle = () => {
    if (!summarized) {
      return;
    }
    setIsExpanded(prev => !prev);
  };

  return (
    <ConditionalDisplay
      dataName="protected-employee-card"
      conditions={[
        {
          showIf: hasProfile,
          reason:
            'Needs additionalData.protected_employee on the selected call',
        },
      ]}
    >
      <section
        className={`${styles.card} ${summarized ? styles.cardSummarized : ''}`}
        data-name="protected-employee-card"
        onClick={summarized ? handleToggle : undefined}
        onKeyDown={
          summarized
            ? event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleToggle();
                }
              }
            : undefined
        }
        role={summarized ? 'button' : undefined}
        tabIndex={summarized ? 0 : undefined}
        aria-expanded={summarized ? isExpanded : undefined}
      >
        <header className={styles.header}>
          <div className={styles.headerText}>
            <h2
              className={styles.title}
              data-name="protected-employee-card-title"
            >
              {PROTECTED_EMPLOYEE_CARD_TITLE}
            </h2>
            {summarized && !isExpanded && organization && (
              <p
                className={styles.source}
                data-name="protected-employee-card-source"
              >
                {organization}
              </p>
            )}
          </div>
          <div className={styles.headerActions}>
            {showDetails && (
              <p
                className={styles.consentLabel}
                data-name="protected-employee-card-consent"
              >
                {PROTECTED_EMPLOYEE_CONSENT_LABEL}
              </p>
            )}
            {summarized && (
              <span className={isExpanded ? styles.chevronExpanded : undefined}>
                <RotatedButton
                  btnType="secondary"
                  size="xsmall"
                  noMargin
                  aria-hidden
                >
                  <ChevronDownIcon width={10} height={10} />
                </RotatedButton>
              </span>
            )}
          </div>
        </header>
        {showDetails && (
          <div className={styles.body} data-name="protected-employee-card-body">
            <div
              className={styles.infoRow}
              data-name="protected-employee-card-info"
            >
              <span className={styles.infoIcon} aria-hidden>
                <InfoIcon width={16} height={16} />
              </span>
              <div className={styles.infoText}>
                <p
                  className={styles.infoName}
                  data-name="protected-employee-card-identifier"
                >
                  {PROTECTED_EMPLOYEE_DISPLAY_NAME}
                </p>
                <p
                  className={styles.infoSubtitle}
                  data-name="protected-employee-card-enrollment"
                >
                  {PROTECTED_EMPLOYEE_ENROLLMENT_SUBTITLE}
                </p>
              </div>
            </div>
            {safetyContactLine && (
              <div
                className={styles.safetyContact}
                data-name="protected-employee-card-safety-contact"
              >
                <p className={styles.safetyContactLabel}>
                  {PROTECTED_EMPLOYEE_SAFETY_CONTACT_LABEL}
                </p>
                <p
                  className={styles.safetyContactValue}
                  data-name="protected-employee-card-safety-contact-value"
                >
                  {safetyContactLine}
                </p>
              </div>
            )}
          </div>
        )}
      </section>
    </ConditionalDisplay>
  );
};

export default ProtectedEmployeeCard;
